import React, { useState } from "react";
import { useReportData } from "../hooks/useReportData";
import { ReportFilterBar } from "../components/ReportFilterBar";
import { ReportKpiCards } from "../components/ReportKpiCards";
import { TransactionGrid } from "../components/TransactionGrid";
import { useSession } from "../hooks/useSession";
import { Alert, Button, Spinner } from "react-bootstrap";

export const ReportsDashboard = () => {
  const { userid, role } = useSession(); 
  const currentUser = { id: userid, role: role }; 
  
  const { filters, updateFilters, loading, error, reportData } = useReportData(currentUser);
  const [selectedReceipt, setSelectedReceipt] = useState(null);

  const handleExportCSV = () => {
    if (!reportData?.transactions?.length) return;
    const headers = Object.keys(reportData.transactions[0]).join(",");
    const rows = reportData.transactions
      .map((row) => Object.values(row).map((v) => `"${v}"`).join(","))
      .join("\n");
    const blob = new Blob([`${headers}\n${rows}`], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute("download", `report_${filters.startDate}_to_${filters.endDate}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <main style={{ backgroundColor: "#f8fafc", minHeight: "100vh", paddingBottom: "40px" }}>
      <div className="container-fluid px-4 py-3">
        {/* Top Header Title Bar & Right User Profile Button */}
        <div className="row align-items-center mb-3">
          <div className="col d-flex align-items-center justify-content-between">
            <div>
              <h3 className="fw-extrabold mb-1" style={{ color: "#0f172a", fontSize: "24px", letterSpacing: "-0.5px" }}>
                Financial & Transaction Reports
              </h3>
              <ul className="d-flex align-items-center gap-2 list-unstyled mb-0" style={{ fontSize: "13.5px" }}>
                <li>
                  <a className="text-decoration-none d-flex align-items-center gap-1.5" href="#" style={{ color: "#64748b", fontWeight: "500" }}>
                    <i className="ph-duotone ph-house-line" style={{ fontSize: "16px", color: "#7e57c2" }}></i>
                  </a>
                </li>
                <li style={{ color: "#94a3b8" }}>/</li>
                <li style={{ color: "#64748b", fontWeight: "500" }}>Reports</li>
                <li style={{ color: "#94a3b8" }}>/</li>
                <li className="fw-bold" style={{ color: "#7e57c2" }}>
                  Active Role: <span style={{ color: "#7e57c2" }}>{reportData.role || currentUser.role || "Retailer"}</span>
                </li>
                {loading && (
                  <Spinner animation="border" size="sm" className="text-primary ms-2" />
                )}
              </ul>
            </div>

            <div>
              <button
                className="btn d-flex align-items-center justify-content-center p-0 border-0 text-white shadow-sm"
                style={{
                  backgroundColor: "#7e57c2",
                  borderRadius: "14px",
                  width: "44px",
                  height: "44px",
                  boxShadow: "0 4px 12px rgba(126, 87, 194, 0.3)"
                }}
              >
                <i className="ph-bold ph-user" style={{ fontSize: "20px" }}></i>
              </button>
            </div>
          </div>
        </div>

        {/* Hero Banner Card with 3D Report Graphic */}
        <div
          className="card border-0 mb-3 position-relative overflow-hidden shadow-sm"
          style={{
            background: "linear-gradient(135deg, #f5efff 0%, #eef2ff 60%, #e0e7ff 100%)",
            borderRadius: "16px"
          }}
        >
          <div className="card-body p-3 px-4 position-relative z-1">
            <div className="row align-items-center">
              <div className="col-12 col-md-7 col-lg-8">
                <div className="d-flex align-items-center gap-2.5">
                  <div
                    className="d-flex align-items-center justify-content-center flex-shrink-0"
                    style={{
                      width: "42px",
                      height: "42px",
                      borderRadius: "12px",
                      backgroundColor: "#f3e8ff",
                      color: "#7e57c2"
                    }}
                  >
                    <i className="ph-duotone ph-file-text" style={{ fontSize: "22px" }}></i>
                  </div>
                  <div>
                    <h5 className="fw-extrabold mb-0.5" style={{ color: "#0f172a", fontSize: "16px" }}>
                      Financial & Transaction Reports
                    </h5>
                    <p className="text-muted mb-0" style={{ fontSize: "12.5px" }}>
                      View, analyse and export your transaction and commission reports.
                    </p>
                  </div>
                </div>
              </div>

              <div className="col-12 col-md-5 col-lg-4 text-end d-none d-md-block">
                <img
                  src="../assets/images/service/commission-slabs-banner.png"
                  onError={(e) => { e.target.src = "/assets/images/service/commission-slabs-banner.png"; }}
                  alt="Reports Banner"
                  className="img-fluid"
                  style={{
                    maxHeight: "65px",
                    objectFit: "contain",
                    mixBlendMode: "multiply"
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="row m-1">
          <div className="col-12">
            {/* Filter Bar */}
            <ReportFilterBar
              filters={filters}
              onFilterChange={updateFilters}
              onExport={handleExportCSV}
              loading={loading}
            />

            {/* Error Banner */}
            {error && (
              <div 
                className="d-flex align-items-center p-3 mb-4 rounded-3 text-white shadow-sm" 
                style={{ backgroundColor: "#7e57c2" }}
              >
                <i className="ph-duotone ph-warning-circle fs-5 me-2"></i> 
                <span className="fw-medium">{error}</span>
              </div>
            )}


            {/* KPI Cards */}
            <ReportKpiCards totals={reportData.grandTotals} role={reportData.role} />

            <TransactionGrid
              transactions={reportData.transactions || []}
              role={reportData.role}
              onSelectReceipt={(tx) => setSelectedReceipt(tx)}
            />

            {/* Modern Pagination Footer */}
            <div
              className="card border-0 shadow-sm mt-4"
              style={{ borderRadius: "20px", backgroundColor: "#ffffff" }}
            >
              <div className="card-body px-4 py-3 d-flex flex-wrap align-items-center justify-content-between gap-3">
                <div className="d-flex align-items-center gap-3">
                  <span className="text-muted small fw-semibold" style={{ fontSize: "13.5px", color: "#64748b" }}>
                    Showing Page{" "}
                    <strong style={{ color: "#0f172a" }}>
                      {reportData.pagination?.page || filters.page || 1}
                    </strong>{" "}
                    of{" "}
                    <strong style={{ color: "#0f172a" }}>
                      {reportData.pagination?.total_pages || 1}
                    </strong>{" "}
                    ({reportData.pagination?.total_rows || (reportData.transactions || []).length} records)
                  </span>

                  <div className="d-flex align-items-center gap-2 border-start ps-3 ms-1" style={{ borderColor: "#e2e8f0" }}>
                    <span className="text-muted small fw-semibold" style={{ fontSize: "13px" }}>
                      Rows:
                    </span>
                    <select
                      className="form-select form-select-sm border-1 py-1 px-2 fw-semibold"
                      style={{ borderRadius: "8px", borderColor: "#cbd5e1", fontSize: "13px", width: "70px" }}
                      value={filters.pageSize || 50}
                      onChange={(e) => updateFilters({ pageSize: Number(e.target.value), page: 1 })}
                    >
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                  </div>
                </div>

                <div className="d-flex align-items-center gap-2">
                  <button
                    className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1.5 px-3 py-1.5 fw-semibold border-1"
                    style={{ borderRadius: "10px", fontSize: "13px", borderColor: "#cbd5e1", color: "#475569" }}
                    disabled={(reportData.pagination?.page || filters.page || 1) <= 1 || loading}
                    onClick={() => updateFilters({ page: (filters.page || 1) - 1 })}
                  >
                    <i className="ph-bold ph-caret-left" style={{ fontSize: "14px" }}></i>
                    <span>Previous</span>
                  </button>

                  <div
                    className="d-flex align-items-center justify-content-center text-white fw-bold shadow-sm"
                    style={{
                      width: "34px",
                      height: "34px",
                      borderRadius: "10px",
                      backgroundColor: "#7e57c2",
                      fontSize: "13.5px"
                    }}
                  >
                    {reportData.pagination?.page || filters.page || 1}
                  </div>

                  <button
                    className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1.5 px-3 py-1.5 fw-semibold border-1"
                    style={{ borderRadius: "10px", fontSize: "13px", borderColor: "#cbd5e1", color: "#475569" }}
                    disabled={
                      (reportData.pagination?.page || filters.page || 1) >= (reportData.pagination?.total_pages || 1) ||
                      loading
                    }
                    onClick={() => updateFilters({ page: (filters.page || 1) + 1 })}
                  >
                    <span>Next</span>
                    <i className="ph-bold ph-caret-right" style={{ fontSize: "14px" }}></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};
export default ReportsDashboard;
